import { Floor } from './types';
import { RUBLE_SIGN, SQ_METER_SIGN, DOT_SIGN } from './constants';
import axios from 'axios';
import toasts from '../components/toasts';

import { APIApartmentComplexRes } from 'api/hooks/use-apartment-complex';
import {
  IPrimaryFacilityRecommendationsResponse,
  ISecondaryFacilityRecommendationsResponse,
} from 'screens/estimate/interfaces';

export interface TFacilityType {
  id: number;
  fullTitle: string;
  shortTitle: string;
  roomCount: number;
  isStudio: boolean;
}

export type TSelectValue = number | string | null | undefined;

export interface TypeItemFacility {
  id: number;
  fullTitle: string;
  shortTitle: string;
}

export interface FacilityTypeSchema {
  id: number;
  fullTitle: string;
  shortTitle: string;
  roomCount?: number;
  isStudio?: boolean;
}
/* ------------------------------------------------------------------------------------ */

export const dotDelim = ' ' + DOT_SIGN + ' ';

/* ------------------------------------------------------------------------------------ */

export const toReadableNumber = (num: number | string, delim = ' ') => {
  return num
    ?.toString()
    .split('')
    .reverse()
    .reduce((acc, curr, i) => {
      if (i !== 0 && i % 3 === 0) {
        return curr + delim + acc;
      }

      return curr + acc;
    }, '');
};

export const toReadableNumber2 = (number?: number | string, showCurrecy?: boolean) => {
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: showCurrecy ? 'currency' : 'decimal',
    currency: 'RUB',
    minimumSignificantDigits: 1,
  });
  return formatter.format(number ? +number : 0).replace(',', '.');
};

export const toReadablePrice = (num: number) => {
  return toReadableNumber(num) + ' ' + RUBLE_SIGN;
};

export const toReadablePriceOptional = (num?: number) => {
  return typeof num === 'number' ? toReadablePrice(num) : '';
};

export const toReadableSquarePrice = (num: number) => {
  return toReadablePrice(num) + ' / ' + SQ_METER_SIGN;
};

export const toPercent = (num: number) => {
  return `${num} %`;
};

export const toPercentOptional = (num?: number) => {
  return typeof num === 'number' ? toPercent(num) : '';
};

export const isInt = (num: number) => num % 1 === 0;

export const toMeters = (num: number) => {
  const str = isInt(num) ? num : num?.toFixed(2).replace('.', ',');
  return `${str} м`;
};

export const formattedStringToInt = (str: string) => {
  return +str.replaceAll(/\D/g, '');
};

export const formattedStringToFloat = (str: string) => {
  let value = str.replace(',', '.').split(/[^0-9,.]/)[0];
  value = value.replaceAll(/[^0-9,.]/g, '');
  return parseFloat(value) || 0;
};

/* ------------------------------------------------------------------------------------ */

export const formatPhone = (str: string) => {
  return str.slice(0, 2) + ' ' + str.slice(2, 5) + ' ' + str.slice(5, 8) + ' ' + str.slice(8);
};

/* ------------------------------------------------------------------------------------ */

const formatNoun = (num: number, single: string, plural: string, pluralEx: string, base = 10) => {
  let noun = plural;
  const rem = num % base;

  if (rem === 1) {
    noun = single;
  } else if (rem === 2 || rem === 3 || rem === 4) {
    noun = pluralEx;
  }

  return toReadableNumber(num) + ' ' + noun;
};

export const formatRoomsWord = (num: number) => {
  return formatNoun(num, 'комната', 'комнат', 'комнаты');
};

export const formatObjectWord = (num: number) => {
  return formatNoun(num, 'объект', 'объектов', 'объекта');
};

export const formatViewObjectsPhrase = (num: number | undefined | null) => {
  return num && num !== 0 ? `Посмотреть ${formatObjectWord(num)}` : 'Объекты не найдены';
};

export const formatYearWord = (num: number) => {
  return formatNoun(num, 'год', 'лет', 'года', 20);
};

export const formatDayWord = (num: number) => {
  return formatNoun(num, 'день', 'дней', 'дня');
};

export const formatDealWord = (num: number) => {
  return formatNoun(num, 'предложение', 'предложений', 'предложения');
};

export const formatRoomsWordWithApartment = (num: number) => {
  return num + '-к. квартира';
};

export const formatArea = (num: number) => {
  const fixed = num?.toFixed(2);
  const split = fixed?.split('.');

  let res = '';
  if (split?.[1] === '00') {
    res += num;
  } else {
    res = fixed?.replace('.', ',');
  }

  return res + ' ' + SQ_METER_SIGN;
};

export const formatAreaOptional = (num?: number) => {
  return typeof num === 'number' ? formatArea(num) : '';
};

export const formatFloor = (obj: Floor) => {
  const { floor, total } = obj;

  const floorPart = typeof floor === 'number' ? floor : floor[0] + '-' + floor[1];

  return floorPart + (total ? '/' + total : '') + ' этаж';
};

export const formatApartmentDesc = (rooms: number | null, area: number, floor: Floor) => {
  return (
    (rooms ? formatRoomsWord(rooms) + dotDelim : '') +
    formatArea(area) +
    dotDelim +
    formatFloor(floor)
  );
};

export const formatApartmentDescAlternative = (rooms: number, area: number, floor: Floor) => {
  return (
    formatRoomsWordWithApartment(rooms) +
    dotDelim +
    formatArea(area) +
    dotDelim +
    formatFloor(floor)
  );
};

export const formatAddress = (
  complexData: Pick<
    APIApartmentComplexRes,
    'geoCity' | 'geoDistrict' | 'geoStreet' | 'geoBuilding'
  >,
) => {
  const { geoCity, geoDistrict, geoStreet, geoBuilding } = complexData;

  const city = geoCity?.name;
  const district = geoDistrict?.name;
  const street = geoStreet ? geoStreet.type + ' ' + geoStreet.name : '';
  const building = geoBuilding ? 'дом ' + geoBuilding.house : '';

  return [city, district, street, building].filter((i) => i).join(', ');
};

export const formatApartmentTitle = (title: string) => {
  return title.replaceAll(',', DOT_SIGN).replace('м2', SQ_METER_SIGN);
};

export const formatNullishValue = <T>(value: T) => {
  return value ?? '-';
};

/* ------------------------------------------------------------------------------------ */
export const toggleSetValue = <T>(set: Set<T>, value: T) => {
  if (set.has(value)) set.delete(value);
  else set.add(value);
};

export const toggleSetValuesArray = <T>(set: Set<number>, values: number[] | number) => {
  if (typeof values !== 'number') {
    values.forEach((item) => {
      if (set.has(item)) {
        set.delete(item);
      } else {
        set.add(item);
      }
    });
  }
};

export const toggleSetArrayValue = (set: Set<number>, arrayValue: number[]) => {
  set.clear();
  return arrayValue.forEach((element: number) => {
    set.add(element);
  });
};

export const getIsLoadingNextPage = (isLoading: boolean, size: number, data?: unknown[]) => {
  return isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
};

/* ------------------------------------------------------------------------------------ */

export const labelify = <T extends { id: number }>(obj: T, prop: keyof T) => {
  return { id: obj.id, label: obj[prop] + '' };
};

/* ------------------------------------------------------------------------------------ */

export const toReadableNumberPrice = (number?: number | string, showCurrecy?: boolean) => {
  const formatter = new Intl.NumberFormat('ru-RU', {
    style: showCurrecy ? 'currency' : 'decimal',
    currency: 'RUB',
    minimumSignificantDigits: 1,
  });
  return formatter.format(number ? +number : 0).replace(',', '.');
};

export const serializeQuery = (data?: Record<string, any> | void, defaultKey?: string) => {
  if (!data) {
    return '';
  }

  return Object.keys(data)
    .reduce<string[]>((parts, key) => {
      if (Array.isArray(data[key]) && data[key].length) {
        parts.push(
          Object.keys(data[key])
            .map((key2) => `${key}[${key2}]=${encodeURIComponent(data[key][key2])}`)
            .join('&'),
        );
      } else if (typeof data[key] === 'string' && data[key].length) {
        parts.push(`${key}=${encodeURIComponent(data[key])}`);
      } else if (typeof data[key] === 'number' || typeof data[key] === 'boolean') {
        parts.push(`${defaultKey ? defaultKey : key}=${data[key]}`);
      }
      return parts;
    }, [])
    .join('&');
};

export type TUserRole = TEmployeeRole | 'Client' | 'Realtor';

export type TEmployeeRole =
  | 'Director'
  | 'Creator'
  | 'Manager'
  | 'Lawyer'
  | 'Accountant'
  | 'Registrant'
  | 'Operator'
  | 'Logistician';

export const userHasAccess = (roles?: TUserRole[], rolesUser?: TUserRole[]) =>
  rolesUser && rolesUser.some((str: TUserRole) => roles && roles.includes(str));

export const downloadFile = (blob: Blob, name: string) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

export const downloadFileByUrl = (url: string, name: string) => {
  axios
    .get<Blob>(url, { responseType: 'blob' })
    .then((res) => downloadFile(res.data, name))
    .catch(() => toasts.error('Ошибка при скачивании файла'));
};

export const isValidISOString = (dateString: string): boolean => {
  // Using a regular expression to validate the date format
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
  return isoDateRegex.test(dateString);
};

interface DateTimeFormatOptions {
  timeZone?: string;
  hour12?: boolean;
  weekday?: 'narrow' | 'short' | 'long';
  year?: 'numeric' | '2-digit';
  month?: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long';
  day?: 'numeric' | '2-digit';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
}

export const isoToKrasnoyarskTime = (isoDateString: string): string | null => {
  // Checking if the passed value is a valid date in ISO format
  if (!isValidISOString(isoDateString)) {
    console.error('Invalid ISO date string:', isoDateString);
    return '-';
  }

  const isoDate = new Date(isoDateString);
  const options: DateTimeFormatOptions = {
    timeZone: 'Asia/Krasnoyarsk',
    year: 'numeric', // Год
    month: 'long', // Месяц (полное название)
    day: 'numeric', // День месяца
    hour: 'numeric', // Часы
    minute: 'numeric', // Минуты
  };

  const formatter = new Intl.DateTimeFormat('ru-RU', options);
  return formatter?.format(isoDate);
};

export const extractYear = (str: string) => {
  // Используем регулярное выражение для поиска 4-значного года
  let match = str?.match(/\d{4}/);
  // Если год найден, возвращаем его, иначе возвращаем '-'
  return match ? match?.[0] : '-';
};

export const findMatchingIdsForMultipleIds = (
  apartments: TFacilityType[],
  targetIds: TSelectValue[],
): number[] => {
  const studioRoomCounts = new Set<number>(); // Для roomCount студий

  const roomCountToIdsMap = new Map<number, Set<number>>();

  // Проходим по целевым ID и заполняем данные
  targetIds.forEach((targetId) => {
    const targetApartment = apartments.find((apartment) => apartment.id === targetId);

    if (targetApartment) {
      const { roomCount, isStudio, id } = targetApartment;

      // Добавляем ID в Set по roomCount
      if (!roomCountToIdsMap.has(roomCount)) {
        roomCountToIdsMap.set(roomCount, new Set<number>());
      }
      roomCountToIdsMap.get(roomCount)?.add(id);

      // Если это студия, запоминаем ее roomCount
      if (isStudio) {
        studioRoomCounts.add(roomCount);
      }
    }
  });

  const filteredMatchingIds = new Set<number>();
  const roomCountsWithApartments = new Set<number>();

  roomCountToIdsMap.forEach((ids, roomCount) => {
    // Проверяем, есть ли квартира с таким roomCount
    const hasMatchingApartment = [...ids].some((id) => {
      const apartment = apartments.find((apartment) => apartment.id === id);
      return apartment;
    });

    if (hasMatchingApartment) {
      if (roomCount === 1) {
        const apartmentId = [...ids].find((id) => {
          const apartment = apartments.find((apartment) => apartment.id === id);
          return apartment && !apartment.isStudio;
        });

        const studioId = [...ids].find((id) => {
          const apartment = apartments.find((apartment) => apartment.id === id);
          return apartment && apartment.isStudio;
        });

        // В противном случае, добавляем только один ID
        if (apartmentId) filteredMatchingIds.add(apartmentId);
        if (studioId) filteredMatchingIds.add(studioId);
      } else {
        // Для остальных roomCount добавляем все ID с этим roomCount
        ids.forEach((id) => filteredMatchingIds.add(id));
        roomCountsWithApartments.add(roomCount);
      }
    }
  });

  // Добавляем студии, соответствующие квартирам, но исключаем студии с roomCount 1
  apartments.forEach((apartment) => {
    if (
      apartment.isStudio &&
      roomCountsWithApartments.has(apartment.roomCount) &&
      apartment.roomCount !== 1 // Исключаем студии с roomCount 1
    ) {
      filteredMatchingIds.add(apartment.id);
    }
  });

  return Array.from(filteredMatchingIds);
};

export const transformTypeFacility = (types: TypeItemFacility[]) => {
  const getRoomNumber = (title: string) => title.split('-')[0].trim();

  // Разделяем квартиры и студии
  const apartmentsOnly = types.filter((type) => !type.fullTitle?.includes('студия'));
  const studiosOnly = types.filter((type) => type.fullTitle?.includes('студия'));

  // Добавляем студии к соответствующим квартирам и оставляем 1-к студию
  return apartmentsOnly.concat(
    studiosOnly.filter((studio) => getRoomNumber(studio.fullTitle) === '1'), // Добавляем 1-к студию в общий список
  );
};

export const removeZeroRoomApartments = (
  apartments: FacilityTypeSchema[],
): FacilityTypeSchema[] => {
  return apartments.filter((apartment) => apartment.roomCount !== 0);
};

interface ISortInterfaceByTitle {
  label: string;
  [key: string]: any;
}

export const sortByTitle = (arr: ISortInterfaceByTitle[]) => {
  return arr.sort(customSort);

  function customSort(a: ISortInterfaceByTitle, b: ISortInterfaceByTitle) {
    const titleA = a.label;
    const titleB = b.label;

    // Паттерн для проверки, начинается ли строка с цифры
    const isNumericA = /^\d/.test(titleA);
    const isNumericB = /^\d/.test(titleB);

    // Если оба заголовка являются строками, начинающимися с букв
    if (!isNumericA && !isNumericB) {
      return titleA.localeCompare(titleB, 'ru');
    }

    // Если один заголовок строка, а другой - число
    if (isNumericA && !isNumericB) {
      return 1; // Числа идут после строк
    }
    if (!isNumericA && isNumericB) {
      return -1; // Строки идут перед числами
    }

    // Если оба заголовка - числа, сравниваем их как числа
    const numA = titleA.match(/^\d+/) ? parseInt(titleA.match(/^\d+/)![0]) : titleA;
    const numB = titleB.match(/^\d+/) ? parseInt(titleB.match(/^\d+/)![0]) : titleB;

    if (numA !== numB) {
      return Number(numA) - Number(numB);
    }

    // Если числа равны, сортируем по полному заголовку
    return titleA.localeCompare(titleB, 'ru');
  }
};

export const getTransformedMinMaxMarketPrice = (price: string) => {
  if (price.length < 7) {
    return 0 + '.' + String(price)[0] + ' ' + 'млн' + ` ${RUBLE_SIGN}`;
  }
  if (price.length === 9) {
    return (
      String(price)[0] +
      String(price)[1] +
      String(price)[2] +
      +'.' +
      String(price)[3] +
      ' ' +
      ' млн' +
      ` ${RUBLE_SIGN}`
    );
  }
  if (price.length === 8) {
    return (
      String(price)[0] + String(price)[1] + '.' + String(price)[2] + ' ' + 'млн' + ` ${RUBLE_SIGN}`
    );
  }
  return String(price)[0] + '.' + String(price)[1] + ' ' + 'млн' + ` ${RUBLE_SIGN}`;
};

export const getName = (fullname: {
  firstName?: string | null;
  lastName?: string | null;
  patronymic?: string | null;
}) => {
  const firstName = fullname?.firstName ? fullname?.firstName : '';
  const secondName = fullname?.lastName ? fullname?.lastName : '';
  return firstName + ' ' + secondName;
};

export const estimateHaveRecommendations = (
  recommendations:
    | ISecondaryFacilityRecommendationsResponse
    | IPrimaryFacilityRecommendationsResponse,
) =>
  Object.keys(recommendations).filter((key) => {
    if ('lowestPricePerSqM' in recommendations) {
      return recommendations[key as keyof ISecondaryFacilityRecommendationsResponse] !== null;
    }
    return recommendations[key as keyof IPrimaryFacilityRecommendationsResponse] !== null;
  }).length > 0;

export const copyToClipboard = async (value: string, successMessage: string = 'Скопировано') => {
  try {
    await navigator.clipboard.writeText(value)
    toasts.success(successMessage)
  }
  catch (error) {
    toasts.error(`Ошибка копирования: ${error}`)
  }
}